import {
  Col,
  DownloadIcon,
  ErrorDialog,
  Grid,
  LightBulbIcon,
  LineChartIcon,
  Menu,
  Row,
  Skeleton,
  Spinner,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import { FileDownload } from "api/schema/insights/spendInsightsCompaniesSchema";
import { Thread } from "api/schema/insights/threads";
import { useCompanies } from "contexts/CompaniesContext";
import { usePublicApiService } from "contexts/PublicApiServiceProvider";
import { useSpendInsightsProductSettings } from "contexts/SpendInsightsProductSettingsContext";
import { useCreateThread } from "hooks/query/useCreateThread";
import { useGetCompany } from "hooks/query/useGetCompany";
import { useGetThreadsInfinite } from "hooks/query/useGetThreads";
import { useSpendInsightsCompanies } from "hooks/query/useSpendInsightsCompanies";
import React, { useCallback, useEffect, useMemo } from "react";
import {
  Link,
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { formatDateAndTimeWithShortString } from "utils/formatDate";
import formatUrl from "utils/formatUrl";

import { AIChat } from "components/AIChat";
import { paths } from "components/App/paths";
import Page from "components/Page";
import NotFound from "pages/NotFound";

import styles from "./CompanySpendInsights.module.scss";
import Sidebar from "./Sidebar";

interface ChatRouteProps {
  isCreatingThread: boolean;
  allThreads: Thread[];
  threadsLoading: boolean;
  hasCreatedThread: boolean;
}

const ChatRoute = ({
  isCreatingThread,
  hasCreatedThread,
  allThreads,
  threadsLoading,
}: ChatRouteProps) => {
  const { companyId, threadId } = useParams<{
    threadId: string;
    companyId: string;
  }>();

  // Hide AIChat component if a new thread is being created so we don't see a flash of loading state
  const isNewThread =
    hasCreatedThread &&
    allThreads.length > 0 &&
    !allThreads.some((thread) => threadId === thread.threadId);

  return isCreatingThread || (!threadsLoading && isNewThread) ? null : (
    <AIChat companyId={companyId} threadId={threadId} />
  );
};

const CompanySpendInsights = () => {
  const { companyId, threadId } = useParams<{
    companyId: string;
    threadId: string | undefined;
  }>();
  const { setActiveCompany } = useCompanies();
  const publicApi = usePublicApiService();
  const {
    data: spendInsightsSettings,
    isLoading: spendInsightsSettingsLoading,
  } = useSpendInsightsProductSettings();

  const {
    data,
    isLoading: companyDataLoading,
    error: companyDataError,
  } = useGetCompany({ companyId });
  const history = useHistory();
  const routeMatch = useRouteMatch([
    paths.spendInsights.company.THREADS,
    paths.spendInsights.company.INSIGHTS,
  ]);

  const { data: company, isLoading: companyLoading } =
    useSpendInsightsCompanies({
      enabled: true,
      queryParams: {
        page: 1,
        pageSize: 1,
        query: companyId,
      },
    });

  const currentReport = company?.results[0].reportsOverview;

  const {
    data: threads,
    refetch: refetchThreads,
    isLoading: threadsLoading,
    isFetching: isFetchingThreads,
    fetchNextPage,
    hasNextPage: hasMoreThreads,
  } = useGetThreadsInfinite({ companyId });

  const {
    mutateAsync: createThread,
    isPending: isCreatingThread,
    error: createThreadError,
    isSuccess: hasCreatedThread,
  } = useCreateThread({ companyId });

  const createThreadAndMessages = useCallback(() => {
    createThread().then((res) => {
      history.replace(
        formatUrl(paths.spendInsights.company.THREAD, {
          companyId,
          threadId: res.threadId,
        })
      );
      refetchThreads();
    });
  }, [createThread, refetchThreads, history, companyId]);

  useEffect(() => {
    if (threads?.pages[0].results.length === 0) {
      createThreadAndMessages();
    }
  }, [threads, threadId, createThread, companyId, createThreadAndMessages]);

  useEffect(() => {
    if (companyDataLoading) {
      setActiveCompany(null);
    }
    if (data) {
      setActiveCompany(data);
    }
  }, [data, companyDataLoading, setActiveCompany]);

  const allThreads = useMemo(
    () => threads?.pages.flatMap((page) => page.results) ?? [],
    [threads]
  );

  const { addToast } = useToast();

  const downloadFile = useCallback(
    async (fileDownload: Promise<FileDownload>) => {
      try {
        const file = await fileDownload;
        const url = window.URL.createObjectURL(file.data as Blob);

        const a = document.createElement("a");
        a.dataset["testid"] = "download-link";
        a.style.display = "none";
        a.href = url;
        a.download = file.fileName;

        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        addToast({
          variant: "success",
          message: "Your download has started",
        });
      } catch (err) {
        addToast({
          variant: "error",
          message: "Failed to download report",
        });
      }
    },
    [addToast]
  );

  const onDownloadReport = async () => {
    if (!currentReport) return;

    try {
      downloadFile(
        publicApi.getSpendReport({
          params: {
            companyId,
            reportType: currentReport.reportType,
            reportId: "latest",
            outputName: "excel",
          },
        })
      );
    } catch (error) {
      addToast({
        variant: "error",
        message: "Failed to download report",
      });
    }
  };

  useEffect(() => {
    if (routeMatch?.isExact && allThreads.length) {
      const [firstThread] = allThreads;
      history.replace(
        formatUrl(paths.spendInsights.company.THREAD, {
          companyId,
          threadId: firstThread.threadId,
        })
      );
    }
  }, [threadId, history, companyId, allThreads, routeMatch]);

  const genAIInsightsEnabled =
    spendInsightsSettingsLoading || spendInsightsSettings?.genAIInsights;

  if (!genAIInsightsEnabled) {
    return <NotFound />;
  }

  if (companyDataError) {
    const { status } = companyDataError?.response ?? {};
    return (
      <ErrorDialog
        statusCode={status}
        message={status === 404 ? "Company not found" : undefined}
      />
    );
  }

  if (createThreadError) {
    return (
      <ErrorDialog
        statusCode={createThreadError?.response?.status}
        message="Failed to create a new conversation"
        correlationId={createThreadError?.response?.headers["x-correlation-id"]}
      />
    );
  }

  const sidebarLoading =
    threadsLoading || (!allThreads.length && isCreatingThread);

  return (
    <Page>
      <div className={styles.header}>
        <Typography
          variant="heading_xxl"
          as="h1"
          data-testid="company-header"
          isSkeleton={companyDataLoading}
          skeletonHeight="2.95rem"
          skeletonWidth="15ch"
          noMargin
        >
          {data?.name}
        </Typography>
      </div>
      <Grid className={styles.content} fullWidth>
        <Row>
          <Col size={3} className={styles.column}></Col>
        </Row>
        <Row>
          <Col size={3} className={styles.column}>
            <div className={styles.sidebarWrapper}>
              <Menu className={styles.summary} data-testid="summary-menu">
                <Menu.Title
                  title={
                    <div className={styles.summaryTitle}>
                      <LightBulbIcon />
                      <span>Spend analysis report</span>
                    </div>
                  }
                />
                {!company || companyLoading ? (
                  <div className={styles.summarySkeleton}>
                    <Skeleton width="100%" height="2.25rem" />
                    <Skeleton width="100%" height="2.25rem" />
                    <Skeleton width="100%" height="2.25rem" />
                  </div>
                ) : (
                  <>
                    <Typography
                      as="div"
                      variant="body_md"
                      className={styles.lastUpdated}
                      noMargin
                    >
                      {`Last updated ${formatDateAndTimeWithShortString(
                        currentReport?.lastUpdatedUtc ?? "",
                        true
                      )}`}
                    </Typography>
                    <Menu.List>
                      <Menu.Item
                        onClick={onDownloadReport}
                        isDisabled={!currentReport}
                      >
                        <div>
                          <DownloadIcon />
                          <span>Download report</span>
                        </div>
                      </Menu.Item>
                      <Menu.Item onClick={undefined}>
                        <Link
                          to={formatUrl(paths.companies.SUMMARY, { companyId })}
                        >
                          <LineChartIcon />
                          <span>View company data</span>
                        </Link>
                      </Menu.Item>
                    </Menu.List>
                  </>
                )}
              </Menu>
              <Sidebar
                isLoadingThreads={sidebarLoading}
                threads={allThreads}
                onCreateThread={createThreadAndMessages}
                isCreatingThread={isCreatingThread}
                hasNextPage={hasMoreThreads}
                fetchNextPage={fetchNextPage}
                isFetchingThreads={isFetchingThreads}
              />
            </div>
          </Col>
          <Col size={9} className={styles.column}>
            <section className={styles.chatWrapper}>
              {isCreatingThread && (
                <div className={styles.loadingAnimation}>
                  <Spinner />
                </div>
              )}
              <Switch>
                <Route
                  path={paths.spendInsights.company.THREAD}
                  component={() => (
                    <ChatRoute
                      hasCreatedThread={hasCreatedThread}
                      isCreatingThread={isCreatingThread}
                      threadsLoading={threadsLoading}
                      allThreads={allThreads}
                    />
                  )}
                />
              </Switch>
            </section>
          </Col>
        </Row>
      </Grid>
    </Page>
  );
};

export default CompanySpendInsights;
